/**
 * Supplier Configuration card — Strategic Supplier, Call-Off and Logistics
 * Provider.
 *
 * Customer review 11 Sep (item 33): both are SUPPLIER-level decisions a
 * customer takes once ("either the supplier is flagged as strategic or not —
 * not at the site levels"), but the card rendered one switch per customer
 * site, so GARDNER AEROSPACE - BENGALURU showed six Strategic and six Call-Off
 * rows that could drift apart. It now shows ONE switch per customer
 * organisation (the site's CompanyGroup; an ungrouped site stands alone) and
 * writes it to every one of that organisation's sites the user administers
 * through ``set-supplier-flag``, in one transaction.
 *
 * The flag is still stored per site pair (the dashboard "Strategic suppliers
 * only" filter reads it that way), so no migration. Reading rule: the switch
 * is ON if ANY site is on — and where sites still disagree (set per site
 * before this change) the card says so beside the switch, so the user knows
 * that switching will make them agree.
 *
 * Logistics Provider is not a relationship column: it is the carrier link from
 * the supplier to each site (read as ``is_logistics_provider``, written through
 * ``set-logistics-provider``). It lives under Call-Off: it is offered only for
 * the sites where this supplier's Call-Off is on (switching Call-Off off
 * switches it off too), and turning it on asks for confirmation because the
 * supplier then sees every supplier's loads.
 */
import { useMemo, useState } from 'react';
import { coreAPI, apiErrorMessage, SupplierCustomerRelationship } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { AlertTriangle, Loader2, PackageCheck, ShieldCheck, Star, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import LogisticsProviderConfirmDialog from './LogisticsProviderConfirmDialog';

type SupplierFlag = 'strategic' | 'call_off_enabled' | 'is_logistics_provider';

const FLAG_META: Record<SupplierFlag, { label: string; description: string; badge: string; badgeClass: string; icon: LucideIcon; iconClass: string }> = {
  strategic: {
    label: 'Strategic Supplier', description: 'Mark this supplier as a strategic vendor',
    badge: 'Strategic', badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    icon: Star, iconClass: 'text-blue-600',
  },
  call_off_enabled: {
    label: 'Call-Off', description: 'Enable call-off and stock management for this supplier',
    badge: 'Enabled', badgeClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
    icon: PackageCheck, iconClass: 'text-teal-600',
  },
  is_logistics_provider: {
    label: 'Logistics Provider',
    description: 'Can see your confirmed, dispatched and delivered call-offs from all suppliers, without prices',
    badge: 'Carrier', badgeClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
    icon: Truck, iconClass: 'text-indigo-600',
  },
};

/** One customer organisation's relationships with this supplier. */
export interface CustomerOrgRelationships {
  key: string;
  /** Organisation (group) name, or the site name for an ungrouped site. */
  label: string;
  relationships: SupplierCustomerRelationship[];
}

/**
 * Bucket relationships by the customer site's organisation.
 * @param rels - every relationship the card lists
 * @returns one bucket per customer group (ungrouped sites alone), by label
 */
export function groupByCustomerOrg(rels: SupplierCustomerRelationship[]): CustomerOrgRelationships[] {
  const byKey = new Map<string, CustomerOrgRelationships>();
  for (const rel of rels) {
    const key = rel.customer_group != null ? `g${rel.customer_group}` : `c${rel.customer}`;
    const bucket = byKey.get(key) ?? { key, label: rel.customer_group_name || rel.customer_name, relationships: [] };
    bucket.relationships.push(rel);
    byKey.set(key, bucket);
  }
  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The switch state for one flag across an organisation's sites.
 * @param rels - the organisation's relationships
 * @param field - the flag
 * @returns ``on`` when any site is on; ``mixed`` when the sites disagree
 */
export function supplierFlagState(rels: SupplierCustomerRelationship[], field: SupplierFlag) {
  const onCount = rels.filter((r) => !!r[field]).length;
  return { on: onCount > 0, onCount, total: rels.length, mixed: onCount > 0 && onCount < rels.length };
}

/**
 * The sites one flag's switch covers for an organisation. Logistics Provider
 * lives under Call-Off, so it covers only the sites where this supplier's
 * Call-Off is on — plus any where it is still on, so it can be switched off.
 * @param org - the organisation's relationships
 * @param field - the flag
 * @returns the organisation narrowed to the sites the switch applies to
 */
export function flagScope(org: CustomerOrgRelationships, field: SupplierFlag): CustomerOrgRelationships {
  if (field !== 'is_logistics_provider') return org;
  return {
    ...org,
    relationships: org.relationships.filter((r) => r.call_off_enabled || !!r.is_logistics_provider),
  };
}

interface SupplierConfigurationCardProps {
  supplierCompanyId: number;
  relationships: SupplierCustomerRelationship[];
  relLoading: boolean;
  /** Helix or customer admin — the server refuses anyone else. */
  canEdit: boolean;
  /** Tenant has Call Off; otherwise the switch is not offered at all. */
  callOffAvailable: boolean;
  /** Merge the rows the server returns after a write. */
  onRelationshipsUpdated: (updated: SupplierCustomerRelationship[]) => void;
}

export function SupplierConfigurationCard({
  supplierCompanyId, relationships, relLoading, canEdit, callOffAvailable, onRelationshipsUpdated,
}: SupplierConfigurationCardProps) {
  const orgs = useMemo(() => groupByCustomerOrg(relationships), [relationships]);
  const [saving, setSaving] = useState<string | null>(null);
  // The organisation awaiting confirmation before Logistics Provider goes on.
  const [confirmOrg, setConfirmOrg] = useState<CustomerOrgRelationships | null>(null);
  const fields: SupplierFlag[] = callOffAvailable
    ? ['strategic', 'call_off_enabled', 'is_logistics_provider']
    : ['strategic'];
  const supplierName = relationships[0]?.supplier_name ?? 'This supplier';

  const setFlag = async (org: CustomerOrgRelationships, field: SupplierFlag, value: boolean) => {
    const { label } = FLAG_META[field];
    setSaving(`${field}:${org.key}`);
    try {
      const customer_ids = org.relationships.map((r) => r.customer);
      const res = field === 'is_logistics_provider'
        ? await coreAPI.setSupplierLogisticsProvider({ supplier: supplierCompanyId, value, customer_ids })
        : await coreAPI.setSupplierRelationshipFlag({ supplier: supplierCompanyId, field, value, customer_ids });
      onRelationshipsUpdated(res.data);
      const n = res.data.length;
      toast.success(`${label} ${value ? 'on' : 'off'} for ${n === 1 ? 'the site' : `all ${n} sites`}`);
    } catch (err: unknown) {
      console.error(`Error updating ${field}:`, err);
      toast.error(apiErrorMessage(err, `Failed to update ${label}`));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Supplier Configuration</CardTitle>
        <CardDescription>Configure supplier status and preferences — each setting applies to every site of the customer</CardDescription>
      </CardHeader>
      <CardContent>
        {relLoading && relationships.length === 0 ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : relationships.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            This supplier is not assigned to a customer site yet. Assign one under Customer Relationships to configure these settings.
          </p>
        ) : (
          <div className="space-y-4">
            {fields.flatMap((field) => orgs.map((fullOrg) => {
              const org = flagScope(fullOrg, field);
              if (org.relationships.length === 0) return null;
              const meta = FLAG_META[field];
              const Icon = meta.icon;
              const state = supplierFlagState(org.relationships, field);
              const busy = saving === `${field}:${org.key}`;
              const siteNames = org.relationships.map((r) => r.customer_name).join(', ');
              return (
                <div key={`${field}-${org.key}`} className="flex items-center justify-between gap-4 p-4 border rounded-lg" data-testid={`supplier-flag-${field}`}>
                  <div className="space-y-0.5 min-w-0">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${meta.iconClass}`} />
                      {meta.label}
                      {orgs.length > 1 && (<span className="text-xs text-muted-foreground font-normal">— {org.label}</span>)}
                    </label>
                    <p className="text-xs text-muted-foreground">{meta.description}</p>
                    <p className="text-xs text-muted-foreground truncate" title={siteNames}>
                      Applies to {state.total === 1 ? '1 site' : `all ${state.total} sites`}: {siteNames}
                    </p>
                    {state.mixed && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1" data-testid="supplier-flag-mixed">
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                        On for {state.onCount} of {state.total} sites (set per site before this became one setting) — shown as on; switching sets all {state.total}.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {state.on && (<Badge className={meta.badgeClass}>{meta.badge}</Badge>)}
                    {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    <Switch
                      aria-label={`${meta.label} — ${org.label}`}
                      checked={state.on}
                      onCheckedChange={(checked) => {
                        if (!canEdit) return;
                        if (field === 'is_logistics_provider' && checked) setConfirmOrg(org);
                        else setFlag(org, field, checked);
                      }}
                      disabled={!canEdit || saving !== null}
                    />
                  </div>
                </div>
              );
            }))}
          </div>
        )}
      </CardContent>
      <LogisticsProviderConfirmDialog
        supplierName={confirmOrg ? supplierName : null}
        orgLabel={confirmOrg?.label ?? ''}
        siteCount={confirmOrg?.relationships.length ?? 0}
        onCancel={() => setConfirmOrg(null)}
        onConfirm={() => {
          const org = confirmOrg;
          setConfirmOrg(null);
          if (org) setFlag(org, 'is_logistics_provider', true);
        }}
      />
    </Card>
  );
}
